import { Badge, Button, Group, Paper, ScrollArea, Stack, Text, Textarea } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { CustomerSafeTicket, TicketSummaryDto } from "@msc/shared";

import { useCustomerBranding } from "../../hooks/useCustomerView.js";
import { useEditSummary } from "../../hooks/usePresentation.js";
import { hasCustomerDesign } from "../../lib/customerView/brandingTheme.js";
import { openStageWindow } from "../../lib/customerView/stageWindow.js";
import { usePresentationStore } from "../../stores/presentation.js";

const MAX_LENGTH = 600;

/**
 * Schritt 2: die erzeugten Zusammenfassungen prüfen, BEVOR das Kundenfenster
 * aufgeht. Das ist der Sinn des Zwischenschritts — was der Kunde als
 * „Aktueller Stand“ liest, wird in Ruhe redigiert und nicht unter Zeitdruck im
 * laufenden Termin.
 *
 * Port aus der Hub-App (`components/PrepReview.tsx`) auf Mantine.
 *
 * Das Kundendesign (D10) wird hier schon geladen — dieselbe Abfrage wie im
 * Cockpit —, damit der Start weiß, ob die Bühne im Design des Kunden beginnt.
 */
export function PrepReview({
  tickets,
  customerObjectKey,
}: {
  tickets: CustomerSafeTicket[];
  customerObjectKey: string;
}) {
  const { t } = useTranslation();
  const branding = useCustomerBranding(customerObjectKey);

  const deck = usePresentationStore((s) => s.deck);
  const summaries = usePresentationStore((s) => s.summaries);
  const setSummary = usePresentationStore((s) => s.setSummary);
  const startShow = usePresentationStore((s) => s.startShow);
  const withSummaries = usePresentationStore((s) => s.withSummaries);
  const backToPrep = usePresentationStore((s) => s.backToPrep);

  const byKey = useMemo(() => new Map(tickets.map((x) => [x.key, x])), [tickets]);

  const openStage = () => {
    startShow(hasCustomerDesign(branding.data));
    // Die Kennung entsteht in `startShow` — deshalb aus dem Store lesen, nicht
    // aus dem (noch alten) Render.
    if (openStageWindow(usePresentationStore.getState().ownerId) === null) {
      notifications.show({ color: "yellow", message: t("pages.customerView.present.stageBlocked") });
    }
  };

  return (
    <Stack gap="sm" style={{ flex: 1, minHeight: 0 }}>
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Group gap="sm" wrap="wrap">
          <Text fw={600}>{t("pages.customerView.present.reviewTitle")}</Text>
          <Text size="sm" c="dimmed">{t("pages.customerView.present.reviewHint")}</Text>
        </Group>
        <Group gap="sm">
          <Button variant="subtle" onClick={backToPrep}>{t("pages.customerView.present.backToPrep")}</Button>
          <Button onClick={openStage}>{t("pages.customerView.present.openStage", { n: deck.length })}</Button>
        </Group>
      </Group>

      {!withSummaries && (
        <Text size="sm" c="dimmed">{t("pages.customerView.present.noSummariesHint")}</Text>
      )}

      <ScrollArea h="calc(100vh - 300px)" style={{ flex: 1, minHeight: 0 }}>
        <Stack gap="sm">
          {deck.map((key, i) => (
            <ReviewRow
              key={key}
              index={i}
              ticketKey={key}
              title={byKey.get(key)?.title ?? key}
              issueUpdatedAt={byKey.get(key)?.updatedIso ?? null}
              summary={summaries[key] ?? null}
              onSaved={setSummary}
            />
          ))}
        </Stack>
      </ScrollArea>
    </Stack>
  );
}

function ReviewRow({
  index,
  ticketKey,
  title,
  issueUpdatedAt,
  summary,
  onSaved,
}: {
  index: number;
  ticketKey: string;
  title: string;
  /** Ticket-Version, für die die Redaktion gilt — der Server verwirft sie sonst nie. */
  issueUpdatedAt: string | null;
  summary: TicketSummaryDto | null;
  onSaved: (dto: TicketSummaryDto) => void;
}) {
  const { t } = useTranslation();
  const edit = useEditSummary();
  const [draft, setDraft] = useState(summary?.text ?? "");
  const dirty = draft !== (summary?.text ?? "");

  const save = () => {
    if (!issueUpdatedAt) return;
    edit.mutate(
      { key: ticketKey, text: draft.trim(), issueUpdatedAt },
      {
        onSuccess: (dto) => {
          onSaved(dto);
          setDraft(dto.text);
          notifications.show({ color: "green", message: t("pages.customerView.present.saved", { key: ticketKey }) });
        },
        onError: () => {
          notifications.show({ color: "red", message: t("pages.customerView.present.saveFailed", { key: ticketKey }) });
        },
      },
    );
  };

  return (
    <Paper withBorder p="sm">
      <Group gap="xs" wrap="nowrap" align="baseline">
        <Text size="xs" c="dimmed" ff="monospace">{index + 1}.</Text>
        <Text size="xs" c="dimmed" ff="monospace">{ticketKey}</Text>
        <Text size="sm" fw={500} lineClamp={1} style={{ flex: 1, minWidth: 0 }}>{title}</Text>
        {summary?.fallback && (
          <Badge size="xs" color="yellow" variant="light">{t("pages.customerView.present.fallbackBadge")}</Badge>
        )}
        {summary?.edited && (
          <Badge size="xs" color="green" variant="light">{t("pages.customerView.present.editedBadge")}</Badge>
        )}
      </Group>
      <Group gap="sm" align="flex-start" mt="xs" wrap="nowrap">
        <Textarea
          style={{ flex: 1, minWidth: 0 }}
          autosize
          minRows={3}
          maxRows={8}
          maxLength={MAX_LENGTH}
          value={draft}
          aria-label={`${ticketKey} ${title}`}
          onChange={(e) => setDraft(e.currentTarget.value)}
        />
        <Stack gap={4} align="stretch">
          <Button
            size="xs"
            disabled={!dirty || draft.trim() === "" || issueUpdatedAt === null}
            loading={edit.isPending}
            onClick={save}
          >
            {t("pages.customerView.present.save")}
          </Button>
          <Text size="xs" c="dimmed" ta="center">
            {t("pages.customerView.present.characters", { n: draft.length, max: MAX_LENGTH })}
          </Text>
        </Stack>
      </Group>
    </Paper>
  );
}
